import { CalendarOff } from 'lucide-react';

import type { MeuFeriado } from '@/api/meusFeriados';
import { Badge } from '@/components/ui/badge';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { Spinner } from '@/components/ui/spinner';
import { Switch } from '@/components/ui/switch';
import { useNotification } from '@/context/NotificationProvider';
import { useDefinirMeuFeriadoAtivo, useMeusFeriados } from '@/hooks/useMeusFeriados';
import { classifyError, notifyMutationError } from '@/lib/errors';
import { descreverJusticas, descreverOnde, descreverQuando } from '@/pages/admin/calendarioForense/calendarioForense';

function situacao(f: MeuFeriado): { texto: string; variante: 'secondary' | 'outline' } | null {
  if (f.sugestao === 'promovida') return { texto: 'Agora no calendário de todos', variante: 'secondary' };
  if (f.sugestao === 'recusada') return { texto: 'Só nesta conta', variante: 'outline' };
  if (f.sugestao === 'pendente') return { texto: 'Em análise para todos', variante: 'outline' };
  return null;
}

function onde(f: MeuFeriado): string {
  // O backend dá o nome pela tabela do IBGE; município fora dela fica pelo código.
  const lugar =
    f.abrangencia === 'municipal' && !f.municipio_nome
      ? `Município IBGE ${f.municipio_ibge ?? '?'}${f.uf ? ` (${f.uf})` : ''}`
      : descreverOnde(f);
  const justicas = descreverJusticas(f.justicas);
  return justicas ? `${lugar}, só na Justiça ${justicas}` : lugar;
}

interface Props {
  podeAlterar: boolean;
}

/**
 * Os feriados e suspensões lançados na conta (Bloco E), ligados e desligados, com a
 * situação da sugestão. Desligar refaz os vencimentos; a promovida não religa.
 */
export function ListaDeFeriados({ podeAlterar }: Props) {
  const { showSuccess, showError } = useNotification();
  const { data, isLoading, error, refetch } = useMeusFeriados();
  const definirAtivo = useDefinirMeuFeriadoAtivo();

  const alternar = (f: MeuFeriado, ativo: boolean) =>
    definirAtivo.mutate(
      { id: f.id, ativo },
      {
        onSuccess: () =>
          showSuccess(ativo ? 'Ligado. Os vencimentos foram refeitos.' : 'Desligado. Os vencimentos foram refeitos.'),
        onError: (err) => notifyMutationError(err, showError),
      },
    );

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Spinner size={32} label="Carregando feriados e suspensões" />
      </div>
    );
  }
  if (error) {
    return (
      <ErrorState
        title="Não deu para carregar os feriados e suspensões"
        description={classifyError(error).message}
        actions={[{ label: 'Tentar de novo', onClick: () => void refetch() }]}
      />
    );
  }
  if (!data?.length) {
    return (
      <EmptyState
        icon={<CalendarOff />}
        title="Nenhum feriado ou suspensão lançado"
        description={
          podeAlterar
            ? 'Lance aqui o dia que a conta do prazo não pegou, ou pela conta de uma intimação, em “Faltou um feriado ou uma suspensão?”.'
            : 'Esta conta ainda não lançou nenhum dia.'
        }
      />
    );
  }

  return (
    <ul className="divide-y divide-border/60">
      {data.map((f) => {
        const s = situacao(f);
        return (
          <li key={f.id} className={`flex items-center gap-3 py-3 ${f.ativo ? '' : 'opacity-60'}`}>
            <div className="min-w-0 flex-1">
              <div className="font-medium break-words">{f.nome}</div>
              <div className="text-sm text-muted-foreground">
                {descreverQuando(f)} · {onde(f)}
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                {s && <Badge variant={s.variante}>{s.texto}</Badge>}
                <span className="break-words">{f.ato_normativo}</span>
              </div>
              {f.sugestao === 'recusada' && f.sugestao_nota && (
                <div className="mt-1 text-xs text-muted-foreground">
                  Motivo de não valer para todos: {f.sugestao_nota}
                </div>
              )}
            </div>
            <label className="flex min-h-11 min-w-11 cursor-pointer items-center justify-end">
              <Switch
                checked={f.ativo}
                // Promovida: a oficial a substitui; religar a duplicaria.
                disabled={!podeAlterar || definirAtivo.isPending || f.sugestao === 'promovida'}
                onCheckedChange={(v) => alternar(f, v)}
                aria-label={`${f.ativo ? 'Desligar' : 'Ligar'} ${f.nome}`}
              />
            </label>
          </li>
        );
      })}
    </ul>
  );
}
